import { ActionIcon, Button, Loader, SegmentedControl, Tooltip } from '@mantine/core';
import { BaseLayout, Icon, sizes, variants } from '@uag/react-core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { useIfcWallEditor } from 'app/modules/ifc-editor/hooks/useIfcWallEditor';
import { createWall, getBounds, storeyPoints } from 'app/modules/ifc-editor/utils/wallGeometry';
import { WallInspector } from 'app/modules/ifc-editor/WallInspector';
import { WallPlanView } from 'app/modules/ifc-editor/WallPlanView';
import { PlanStartRoute } from 'app/modules/routePaths';

import type { EditorSelection } from 'app/modules/ifc-editor/types/buildingSpecification';

const isTyping = (target: EventTarget | null) =>
    target instanceof HTMLElement &&
    (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));

/** IFC wall editing mode: a dimensioned plan of a generated model whose walls, openings and slabs can be adapted. */
export const IfcWallEditorPage = () => {
    const { t } = useTranslation();
    const { ifcModelId = '' } = useParams();
    const editor = useIfcWallEditor(ifcModelId);
    const { specification, apply, checkpoint, undo, redo, canUndo, canRedo, isDirty } = editor;
    const [storeyChoice, setStoreyChoice] = useState('');
    const [selectionChoice, setSelectionChoice] = useState<EditorSelection>(null);
    const [showDimensions, setShowDimensions] = useState(true);
    const [fitCounter, setFitCounter] = useState(0);

    const storeyNames = useMemo(() => specification?.storeys.map((candidate) => candidate.name) ?? [], [specification]);
    // Falls back to the first storey with walls when the chosen one was renamed or does not exist yet.
    const storey = storeyNames.includes(storeyChoice)
        ? storeyChoice
        : (storeyNames.find((name) => specification?.walls.some((wall) => wall.storey === name)) ??
          storeyNames[0] ??
          '');
    // A selection can point past the end after undo/delete.
    const selection =
        (selectionChoice?.type === 'wall' && !specification?.walls[selectionChoice.index]) ||
        (selectionChoice?.type === 'slab' && !specification?.slabs[selectionChoice.index])
            ? null
            : selectionChoice;

    useEffect(() => {
        if (!isDirty) {
            return;
        }
        const warn = (event: BeforeUnloadEvent) => event.preventDefault();
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [isDirty]);
    const confirmLeave = (event: { preventDefault: () => void }) => {
        if (isDirty && !window.confirm(t('infoWallEditorUnsavedChanges'))) {
            event.preventDefault();
        }
    };

    const deleteSelection = useCallback(() => {
        if (!selection) {
            return;
        }
        apply((current) =>
            selection.type === 'wall'
                ? { ...current, walls: current.walls.filter((_, index) => index !== selection.index) }
                : { ...current, slabs: current.slabs.filter((_, index) => index !== selection.index) }
        );
        setSelectionChoice(null);
    }, [apply, selection]);

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (isTyping(event.target)) {
                return;
            }
            const key = event.key.toLowerCase();
            if ((event.ctrlKey || event.metaKey) && key === 'z') {
                event.preventDefault();
                if (event.shiftKey) {
                    redo();
                } else {
                    undo();
                }
            } else if ((event.ctrlKey || event.metaKey) && key === 'y') {
                event.preventDefault();
                redo();
            } else if ((event.ctrlKey || event.metaKey) && key === 's') {
                event.preventDefault();
                void editor.save();
            } else if (event.key === 'Delete') {
                deleteSelection();
            } else if (event.key === 'Escape') {
                setSelectionChoice(null);
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [deleteSelection, editor, redo, undo]);

    const addWall = () => {
        if (!specification || !storey) {
            return;
        }
        const bounds = getBounds(storeyPoints(specification, storey));
        const wall = createWall(specification, storey, {
            x: Number.isFinite(bounds.minX) ? (bounds.minX + bounds.maxX) / 2 - 1.5 : 0,
            y: Number.isFinite(bounds.minY) ? (bounds.minY + bounds.maxY) / 2 : 0
        });
        apply((current) => ({ ...current, walls: [...current.walls, wall] }));
        setSelectionChoice({ type: 'wall', index: specification.walls.length });
    };

    const content = () => {
        if (editor.isLoading) {
            return (
                <div className="flex h-full items-center justify-center">
                    <Loader />
                </div>
            );
        }
        if (editor.loadError || !specification) {
            return (
                <div className="flex h-full flex-col items-center justify-center gap-3 text-sm" role="alert">
                    {editor.loadError ?? t('errorWallEditorLoad')}
                    <Link className="underline" to={`/${PlanStartRoute}`}>
                        {t('buttonBackToPlans')}
                    </Link>
                </div>
            );
        }

        return (
            <div className="flex h-full min-h-0 flex-col" data-testid="ifc-wall-editor">
                <div className="flex flex-wrap items-center gap-2 border-b border-border-light bg-background-default px-3 py-2">
                    <Link
                        aria-label={t('buttonBackToPlans')}
                        className="flex items-center"
                        to={`/${PlanStartRoute}`}
                        onClick={confirmLeave}
                    >
                        <Icon icon="arrow_back" />
                    </Link>
                    <input
                        aria-label={t('labelBuildingName')}
                        className="h-8 w-56 rounded-sm border border-transparent px-2 text-base font-semibold hover:border-border-light focus:border-border-accent"
                        maxLength={200}
                        value={specification.name}
                        onChange={(event) => {
                            const name = event.target.value;
                            apply((current) => ({ ...current, name }), true);
                        }}
                        onFocus={checkpoint}
                    />
                    {storeyNames.length > 1 && (
                        <SegmentedControl
                            aria-label={t('labelStorey')}
                            data={storeyNames}
                            size="xs"
                            value={storey}
                            onChange={(value) => {
                                setStoreyChoice(value);
                                setSelectionChoice(null);
                            }}
                        />
                    )}
                    <div className="flex items-center gap-1">
                        <Tooltip label={t('buttonUndo')}>
                            <ActionIcon
                                aria-label={t('buttonUndo')}
                                disabled={!canUndo}
                                variant="subtle"
                                onClick={undo}
                            >
                                undo
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label={t('buttonRedo')}>
                            <ActionIcon
                                aria-label={t('buttonRedo')}
                                disabled={!canRedo}
                                variant="subtle"
                                onClick={redo}
                            >
                                redo
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label={t('buttonWallEditorFit')}>
                            <ActionIcon
                                aria-label={t('buttonWallEditorFit')}
                                variant="subtle"
                                onClick={() => setFitCounter((value) => value + 1)}
                            >
                                fit_screen
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label={t('buttonWallEditorDimensions')}>
                            <ActionIcon
                                aria-label={t('buttonWallEditorDimensions')}
                                aria-pressed={showDimensions}
                                variant={showDimensions ? 'filled' : 'subtle'}
                                onClick={() => setShowDimensions((value) => !value)}
                            >
                                straighten
                            </ActionIcon>
                        </Tooltip>
                    </div>
                    <Button
                        leftSection={<Icon aria-hidden="true" icon="add" />}
                        size={sizes.button.small}
                        variant={variants.button.secondary}
                        onClick={addWall}
                    >
                        {t('buttonWallEditorAddWall')}
                    </Button>
                    <div className="ml-auto flex items-center gap-2">
                        <span className="text-xs text-text-light" data-testid="wall-editor-state">
                            {isDirty ? t('infoWallEditorUnsaved') : t('infoWallEditorSaved')}
                        </span>
                        <Button
                            disabled={!isDirty}
                            leftSection={<Icon aria-hidden="true" icon="save" />}
                            loading={editor.isSaving}
                            size={sizes.button.small}
                            variant={variants.button.secondary}
                            onClick={() => void editor.save()}
                        >
                            {t('buttonWallEditorSave')}
                        </Button>
                        <Button
                            leftSection={<Icon aria-hidden="true" icon="view_in_ar" />}
                            loading={editor.isSaving}
                            size={sizes.button.small}
                            onClick={() => void editor.openInWallForming()}
                        >
                            {isDirty ? t('buttonWallEditorSaveAndForm') : t('buttonStartWallForming')}
                        </Button>
                    </div>
                </div>

                {(editor.saveProblem || editor.warnings.length > 0) && (
                    <div
                        className={`px-3 py-2 text-xs ${editor.saveProblem ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-800'}`}
                        data-testid="wall-editor-messages"
                        role={editor.saveProblem ? 'alert' : 'status'}
                    >
                        {editor.saveProblem && <div className="font-medium">{editor.saveProblem.detail}</div>}
                        <ul className="max-h-24 list-disc overflow-auto pl-4">
                            {(editor.saveProblem?.errors ?? editor.warnings).map((message) => (
                                <li key={message}>{message}</li>
                            ))}
                        </ul>
                    </div>
                )}

                <div className="flex min-h-0 flex-1">
                    <div className="relative min-w-0 flex-1">
                        <WallPlanView
                            fitKey={`${ifcModelId}-${fitCounter}`}
                            selection={selection}
                            showDimensions={showDimensions}
                            specification={specification}
                            storey={storey}
                            onApply={apply}
                            onCheckpoint={checkpoint}
                            onSelect={setSelectionChoice}
                        />
                        <div className="pointer-events-none absolute bottom-2 left-2 rounded-sm bg-white/80 px-2 py-1 text-xs text-text-light">
                            {t('infoWallEditorHelp')}
                        </div>
                    </div>
                    <aside className="w-80 shrink-0 overflow-auto border-l border-border-light bg-background-default">
                        <WallInspector
                            selection={selection}
                            specification={specification}
                            storey={storey}
                            onApply={(update) => apply(update)}
                            onSelect={setSelectionChoice}
                        />
                    </aside>
                </div>
            </div>
        );
    };

    return (
        <div className="max-h-screen max-w-screen overflow-hidden">
            <BaseLayout
                className="bg-background-gray-ultra-ultra-light overflow-hidden"
                environment={import.meta.env.VITE_ENVIRONMENT}
                innerClassName="h-full w-full overflow-hidden"
                showFooter={true}
                versionNumber={APP_VERSION}
                withCookieBanner={false}
            >
                {content()}
            </BaseLayout>
        </div>
    );
};
